import { Box, Chip, Stack, Tooltip } from '@mui/material';
import React, { useMemo, useState } from 'react';
import ElementDataRow from 'components/common/element-data/ElementDataRow';
import {
    ElementMetadataFieldName,
    ElementTopicSection,
    IElementDataGlossaryTerms,
    Topic,
} from 'components/common/element-data/types';
import { useQuery } from '@tanstack/react-query';
import styles from '../ElementData.styles';
import IconMi from 'components/common/icon/IconMi';
import { APIResponse } from 'tools/types';
import { editorTopicsAPI, TopicsInfo } from 'api/editor/topics';
import IconHandler from 'components/common/icon/IconHandler';
import { dispatchCustomEvent } from 'tools/events';
import GlossaryTermPopup from 'components/glossary-term-popup';
import { adjustTopicsDataFormat, getFieldElementMetadataSettings, useTopicsLink } from '../functions';
import { useGlossaryTermVisibility } from 'hooks/useGlossaryTerm';
import { IGlossarySettings } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import { openInNewTab } from 'tools/tools';
import { useSelector } from 'react-redux';

export default function GlossaryTerms(props: IElementDataGlossaryTerms) {
    const {
        elementId = 0,
        topicsNeedToLoad = false,
        topicIdsToLoad = [],
        topics: topicsData = {},
        glossaryTermPopupProps,
        settings,
    } = props;
    const fieldKey: ElementMetadataFieldName = 'glossary_terms';
    const { fieldMetadataDisplay, fieldMetadataOrder } = getFieldElementMetadataSettings(settings, fieldKey);

    const [collapseTermSectionList, setCollapseTermSectionList] = useState<any>({});
    const { isVisibleGlossaryTermByStatus } = useGlossaryTermVisibility();

    const glossarySettings: IGlossarySettings | undefined = useAppSelector(
        (state) => state.auth?.configuration?.glossarySettings,
    );
    const hostname = useSelector((state: any) => state.auth?.userInfo?.hostname) ?? '';

    const { data: loadedTopics, isLoading } = useQuery<APIResponse<TopicsInfo>, Error>({
        queryKey: [`tile-${elementId}-topics`],
        queryFn: () => {
            return editorTopicsAPI.getTopicsInfo(topicIdsToLoad);
        },
        enabled: fieldMetadataDisplay && topicsNeedToLoad && topicIdsToLoad.length > 0,
    });

    const toggleCollapseTermSection = (sectionKey: string) => {
        const newCollapseSectionsList = { ...collapseTermSectionList };
        newCollapseSectionsList[sectionKey] = !newCollapseSectionsList?.[sectionKey];
        setCollapseTermSectionList(newCollapseSectionsList);
    };

    const topics: any[] = useMemo((): any => {
        if (topicsNeedToLoad) {
            if (isLoading || !loadedTopics) return {};

            return loadedTopics?.data ? adjustTopicsDataFormat(loadedTopics.data) : {};
        }
        return adjustTopicsDataFormat(topicsData);
    }, [loadedTopics, isLoading, topicsData]);

    const termsSectionKeys = Object.keys(topics)
        .filter((key) => key != 'default-topic-type')
        .sort((a: any, b: any) => {
            const termA = topics[a].displayOrder;
            const termB = topics[b].displayOrder;

            return termA - termB;
        })
        .filter((key: any) => {
            const sectionTopics = topics[key]?.topics ?? [];
            return sectionTopics.some((topic: any) => {
                return isVisibleGlossaryTermByStatus(topic);
            });
        });

    const { handleTopicClick, showTermsPopup, selectedTopicId, topicIds, setShowTermsPopup } = useTopicsLink(
        Object.values(topics).reduce(
            (acc: Array<Topic>, section: ElementTopicSection) => acc.concat(section.topics),
            [],
        ),
    );
    const onTagClick = (tag: any, sectionName?: string) => {
        if (settings?.isAppPanel) {
            dispatchCustomEvent('mi-concierge-panel', {
                action: 'term-click',
                payload: {
                    item: tag,
                    sectionName,
                },
            });
        } else {
            if (glossarySettings?.link_to_custom && glossarySettings?.custom_url_pattern > '') {
                openInNewTab(
                    glossarySettings.custom_url_pattern
                        .replaceAll('[hostname]', hostname)
                        .replaceAll('[term id]', tag.topicId),
                );
            } else if (glossaryTermPopupProps) {
                glossaryTermPopupProps.setTopicId(tag.topicId);
                glossaryTermPopupProps.setShowTermsPopup(true);
                glossaryTermPopupProps.setElementId(elementId);
            } else {
                handleTopicClick(false, tag);
            }
        }
    };

    if (!fieldMetadataDisplay) return null;

    if (Object.keys(topics).length == 0 || termsSectionKeys.length == 0) {
        return null;
    }

    return (
        <>
            <ElementDataRow order={fieldMetadataOrder}>
                <Stack width={1} spacing={0.5}>
                    {termsSectionKeys.map((sectionKey: any) => {
                        const section = topics[sectionKey];
                        return (
                            <Box
                                width={1}
                                sx={styles.collapseExpandItem}
                                className={`collapse-expand-item collapse-expand-item--state-${collapseTermSectionList?.[sectionKey] ? 'collapse' : 'expand'}`}
                                key={`info-tag-${sectionKey}-wrapper`}
                            >
                                <Stack
                                    direction="row"
                                    className={'element-data-glossary-terms-header collapse-expand-item__header'}
                                    onClick={() => {
                                        toggleCollapseTermSection(sectionKey);
                                    }}
                                >
                                    <Box sx={styles.labelIcon}>
                                        {section.useIcon === 'fa' && (
                                            <IconHandler icon={{ type: 'fa', value: section.faIcon }} />
                                        )}
                                        {section.useIcon === 'uploaded' && (
                                            <IconHandler
                                                icon={{
                                                    type: 'upload',
                                                    value: `data:image/png;base64, ${section.icon}`,
                                                }}
                                            />
                                        )}
                                    </Box>

                                    <Box flexGrow={1}>{section.topicTypeName}</Box>
                                    <Box sx={{ ...styles.labelIcon, ...{ mr: 0, ml: 1 } }}>
                                        <IconMi
                                            icon={
                                                collapseTermSectionList?.[sectionKey] ? 'chevron-right' : 'chevron-down'
                                            }
                                        />
                                    </Box>
                                </Stack>
                                <Stack
                                    direction="row"
                                    flexWrap={'wrap'}
                                    sx={{ pl: '24px' }}
                                    className={'collapse-expand-item__children'}
                                >
                                    <Stack direction="row" flexWrap={'wrap'} sx={{ ml: -0.5 }}>
                                        {section.topics
                                            .filter((term: any) => isVisibleGlossaryTermByStatus(term))
                                            .map((tag: any) => {
                                                const needToCutLabel = tag.name.length > 30;
                                                const label = needToCutLabel
                                                    ? tag.name.substring(0, 30) + '...'
                                                    : tag.name;
                                                return (
                                                    <Box sx={{ mt: 0.5, ml: 0.5 }} key={tag.topicId}>
                                                        <Tooltip
                                                            key={`info-tag-tooltip-${tag.topicId}`}
                                                            title={needToCutLabel ? tag.name : ''}
                                                        >
                                                            <Chip
                                                                key={`info-tag-${tag.topicId}`}
                                                                onClick={() => onTagClick(tag, section.topicTypeName)}
                                                                label={label}
                                                                color="primary"
                                                            />
                                                        </Tooltip>
                                                    </Box>
                                                );
                                            })}
                                    </Stack>
                                </Stack>
                            </Box>
                        );
                    })}
                </Stack>
            </ElementDataRow>

            {showTermsPopup && (
                <GlossaryTermPopup
                    setShowPopup={setShowTermsPopup}
                    showPopup={showTermsPopup}
                    topicIds={topicIds}
                    topicId={selectedTopicId}
                />
            )}
        </>
    );
}
